// Objective system. Gating which player actions count as "mission
// complete", and tracking incremental progress for the HUD.

import { dist } from '../lib/geometry.js';
import type { World } from '../world/types.js';

const PICKUP_RADIUS = 18;

/** Pick up any collectibles overlapping the player. Sets `picked=true`
 * and starts the pickup-animation timer. Called once per frame. */
export function updatePickups(world: World): void {
  if (world.player.inBox) return;
  for (const c of world.collectibles) {
    if (c.picked) continue;
    if (dist(world.player, c) < PICKUP_RADIUS) {
      c.picked = true;
      c.pickupT = 0.6; // animation duration
    }
  }
  // Decay pickup animations.
  for (const c of world.collectibles) {
    if (c.pickupT > 0) c.pickupT = Math.max(0, c.pickupT - 1 / 60);
  }
}

export interface ObjectiveProgress {
  /** Short text shown next to the 🎯 icon in the HUD. */
  label: string;
  /** True when the player can extract right now. */
  done: boolean;
  /** True when the extraction pad should look "locked" (objective
   * unfinished). Drives the renderer's exit-pad styling. */
  extractLocked: boolean;
}

/** Compute the current objective state. Pure read — no mutation. */
export function objectiveProgress(world: World): ObjectiveProgress {
  switch (world.objective.kind) {
    case 'extract':
      return {
        label: "Atteindre l'extraction",
        done: true,
        extractLocked: false,
      };
    case 'collect-and-extract': {
      const total = world.collectibles.length;
      const got = world.collectibles.filter((c) => c.picked).length;
      const done = got >= total;
      return {
        label: done
          ? `Renseignements: ${got}/${total} ✓ — extraction !`
          : `Renseignements: ${got}/${total}`,
        done,
        extractLocked: !done,
      };
    }
    case 'eliminate-and-extract': {
      const total = world.guards.length;
      const ko = world.guards.filter((g) => g.ko).length;
      const done = ko >= total;
      return {
        label: done
          ? `Gardes neutralisés: ${ko}/${total} ✓ — extraction !`
          : `Neutraliser tous les gardes: ${ko}/${total}`,
        done,
        extractLocked: !done,
      };
    }
  }
}

/** True when the player is on the extraction pad AND the objective is
 * complete. Used as the win predicate by player.ts. */
export function canExtract(world: World, exitRadius: number): boolean {
  if (world.player.inBox) return false;
  if (dist(world.player, world.exitPos) >= exitRadius) return false;
  return objectiveProgress(world).done;
}
